import { apiService } from './apiService';
import { recoService } from './recoService';

/**
 * Explore surface API client (curiosity/observation browse).
 * Mirrors the house axios/auth conventions (apiService adds the bearer token,
 * silent-refresh interceptor, and base URL). Every method fails soft — an error
 * or missing body returns an empty/neutral value so the UI can render a playful
 * empty state instead of throwing.
 *
 * Backed by GET/POST /api/explore/* (templates, rooms, peek, surprise, events).
 */

/** A discoverable authored world (scene template) card. */
export interface ExploreTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  subCategory?: string | null;
  /** Raw tags — may be a CSV string, a JSON array string, or null. Parse via parseTags(). */
  tags?: string | null;
  thumbnailUrl?: string | null;
  minPlayers?: number;
  blurb?: string | null;
  /** ISO timestamp; drives the "New" badge (< 14 days old). */
  createdAt?: string | null;
  /** party | hunt | maze | escape | race | quiz | (empty for non-game worlds). */
  gameKind?: string | null;
}

/**
 * Peek-visibility-gated occupant (V2 Amethyst WU-02/WU-03) — displayName is the
 * real name only when the viewer is entitled to see this person (self / everyone
 * / matched), initials otherwise; photoUrl is null whenever not entitled.
 * sharedInterestCount follows the same gate (never leaked for initials-only
 * occupants, NPCs, or the viewer's own row) — null means "not computed/not
 * entitled", not "zero shared interests". This is the exact shape the backend's
 * `OccupantView` serializes as, reused verbatim for both `ExploreRoom.occupants`
 * and `ExplorePeek.players` (same server type, same wire shape).
 */
export interface ExploreOccupant {
  displayName: string;
  isNpc: boolean;
  photoUrl?: string | null;
  sharedInterestCount?: number | null;
}

/** A live/discoverable room (lobby) card. */
export interface ExploreRoom {
  lobbyId: string;
  name: string;
  joinCode?: string | null;
  category: string;
  subCategory?: string | null;
  sceneTemplateId?: string | null;
  humanCount: number;
  npcCount: number;
  maxPlayers: number;
  hasPassword: boolean;
  requiresApproval: boolean;
  createdAt: string;
  /** The authored world's display name (falls back to `name` when templateless). */
  templateName?: string | null;
  thumbnailUrl?: string | null;
  blurb?: string | null;
  gameKind?: string | null;
  /** Peek-visibility-gated roster (WU-03 R2/R5) — drives the card's people row. */
  occupants: ExploreOccupant[];
}

/** The template metadata `peekRoom` nests inline (a second SceneTemplates read, kept
 * separate from ExploreTemplate since the peek endpoint doesn't project createdAt). */
export interface ExplorePeekTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  subCategory?: string | null;
  tags?: string | null;
  thumbnailUrl?: string | null;
  minPlayers?: number;
  blurb?: string | null;
  gameKind?: string | null;
}

/**
 * Response shape of `GET /api/explore/peek/{lobbyId}` (mirrors ExploreController.Peek
 * verbatim — this does NOT extend ExploreRoom: the endpoint nests template metadata
 * under `template` rather than flattening it, and has no `occupants`/`templateName`/
 * `thumbnailUrl`/`blurb` at the top level). `players` is the live roster gated by
 * peek-visibility (null when the room isn't live in memory — the DB-fallback path).
 */
export interface ExplorePeek {
  live: boolean;
  lobbyId: string;
  name: string;
  joinCode?: string | null;
  category: string;
  subCategory?: string | null;
  sceneTemplateId?: string | null;
  humanCount: number;
  npcCount: number;
  maxPlayers: number;
  hasPassword: boolean;
  requiresApproval: boolean;
  createdAt: string;
  gameKind?: string | null;
  template?: ExplorePeekTemplate | null;
  players: ExploreOccupant[] | null;
}

/** "Tonight" rail card over a ScheduledEvent (WU-03 R6). */
export interface ExploreUpcomingEvent {
  id: string;
  title: string;
  sceneTemplateId?: string | null;
  startsAtUtc: string;
  recurrenceCron?: string | null;
  scope: string;
  templateName?: string | null;
  category?: string | null;
  thumbnailUrl?: string | null;
  gameKind?: string | null;
}

/** An ExploreRoom enriched with WU-05 relevance signal, once ranked. */
export interface RankedRoom extends ExploreRoom {
  /** User-visible reason lines (shared interests, group size, recency) — never raw scores. */
  relevanceReasons: string[];
  /** 0..1 composite relevance, only present when the WU-05 reco surface answered. */
  relevanceScore?: number;
  /** True when this slot is a serendipity pick rather than a top-ranked one. */
  isExplorePick?: boolean;
}

export interface RankedRoomsResult {
  /** "MatchingSignal" (WU-05 answered) | "OccupancyFallback" (WU-05 absent/404). */
  basis: string;
  rooms: RankedRoom[];
}

/** Result of surpriseMe — either a live room to drop into or a world to open. */
export interface ExploreSurprise {
  type: 'room' | 'template' | 'none';
  room?: ExploreRoom | null;
  template?: ExploreTemplate | null;
}

/** Shared query knobs for the templates/rooms browse endpoints. */
export interface ExploreQuery {
  search?: string;
  /** 'all' is treated as no filter. */
  category?: string;
  /** party | hunt | maze | escape | race | quiz. */
  gameKind?: string;
  /** Re-randomize server ordering (shuffle button). */
  random?: boolean;
  /** Rooms endpoint only: include QA/test rooms in the result (caller then splits client-side, same law as isQaRoom). */
  includeQa?: boolean;
}

/**
 * Parse a raw tags value defensively. Accepts a JSON array string
 * (`["a","b"]`), a comma/pipe-separated string, or null → always returns a
 * clean string[] (never throws).
 */
export function parseTags(raw?: string | null): string[] {
  if (!raw) return [];
  const trimmed = raw.trim();
  if (!trimmed) return [];
  if (trimmed.startsWith('[')) {
    try {
      const arr = JSON.parse(trimmed);
      if (Array.isArray(arr)) {
        return arr.map((t) => String(t).trim()).filter(Boolean);
      }
    } catch {
      /* fall through to delimiter parsing */
    }
  }
  return trimmed
    .split(/[,|]/)
    .map((t) => t.trim())
    .filter(Boolean);
}

function buildParams(params?: ExploreQuery): string {
  const q = new URLSearchParams();
  if (params?.search) q.set('search', params.search);
  if (params?.category && params.category !== 'all') q.set('category', params.category);
  if (params?.gameKind) q.set('gameKind', params.gameKind);
  if (params?.random) q.set('random', 'true');
  if (params?.includeQa) q.set('includeQa', 'true');
  const s = q.toString();
  return s ? `?${s}` : '';
}

class ExploreService {
  /** Discoverable authored worlds. */
  async getExploreTemplates(params?: ExploreQuery): Promise<ExploreTemplate[]> {
    const res = await apiService.get<ExploreTemplate[]>(`/api/explore/templates${buildParams(params)}`);
    if (res.error || !res.data) return [];
    return res.data;
  }

  /** Live/discoverable rooms. */
  async getExploreRooms(params?: ExploreQuery): Promise<ExploreRoom[]> {
    const res = await apiService.get<ExploreRoom[]>(`/api/explore/rooms${buildParams(params)}`);
    if (res.error || !res.data) return [];
    return res.data;
  }

  /** Peek at a room's current roster (the observe mechanic). */
  async peekRoom(lobbyId: string): Promise<ExplorePeek | null> {
    const res = await apiService.get<ExplorePeek>(`/api/explore/peek/${encodeURIComponent(lobbyId)}`);
    if (res.error || !res.data) return null;
    return res.data;
  }

  /** Roll the dice — surfaces a random live room or a world to open. */
  async surpriseMe(category?: string): Promise<ExploreSurprise> {
    const q = category && category !== 'all' ? `?category=${encodeURIComponent(category)}` : '';
    const res = await apiService.get<ExploreSurprise>(`/api/explore/surprise${q}`);
    if (res.error || !res.data) return { type: 'none' };
    return res.data;
  }

  /** Upcoming non-cancelled scheduled events (the "Tonight" rail). */
  async getUpcomingEvents(params?: { scope?: string; withinHours?: number }): Promise<ExploreUpcomingEvent[]> {
    const q = new URLSearchParams();
    if (params?.scope) q.set('scope', params.scope);
    if (params?.withinHours) q.set('withinHours', String(params.withinHours));
    const s = q.toString();
    const res = await apiService.get<ExploreUpcomingEvent[]>(`/api/explore/events/upcoming${s ? `?${s}` : ''}`);
    if (res.error || !res.data) return [];
    return res.data;
  }

  /**
   * Ranking seam (WU-03 R4): rank an already-fetched room list for the news
   * page's Hero/Live-now/For-you sections.
   *
   * Tries `POST /api/reco/rooms` (WU-05's matching-ranked surface) first — when
   * it answers (not deployed/flagged-off = null, per recoService's soft-fail
   * contract), each reco entry is joined back onto its ExploreRoom by lobbyId
   * (an entry whose room isn't in `rooms` — e.g. it just closed — drops out
   * silently) and `basis` is threaded straight through.
   *
   * When WU-05 is absent, falls back to a purely client-side sort so the news
   * page never depends on the matching engine to render: humans-desc, then
   * matches-in-room (no signal pre-WU-05, so a no-op tiebreak today — kept as
   * an explicit stage so this fallback needs no changes once match counts
   * exist), then recency (createdAt desc). This is the WU-03 contract: the
   * page must be fully usable with WU-05 undeployed.
   */
  async getRankedRooms(rooms: ExploreRoom[]): Promise<RankedRoomsResult> {
    const reco = await recoService.getRoomRecommendations(Math.max(rooms.length, 12));
    if (reco) {
      const byId = new Map(rooms.map((r) => [r.lobbyId, r]));
      const ranked = reco.rooms
        .map((rr): RankedRoom | null => {
          const room = byId.get(rr.lobbyId);
          if (!room) return null;
          return {
            ...room,
            relevanceReasons: rr.relevance.reasons,
            relevanceScore: rr.relevance.score,
            isExplorePick: rr.relevance.explore,
          };
        })
        .filter((r): r is RankedRoom => r !== null);
      return { basis: reco.basis, rooms: ranked };
    }

    const fallback = [...rooms]
      .sort((a, b) => {
        if (b.humanCount !== a.humanCount) return b.humanCount - a.humanCount;
        // matches-in-room tiebreak arrives with WU-05; no signal to sort on yet.
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      })
      .map((room): RankedRoom => ({ ...room, relevanceReasons: [] }));
    return { basis: 'OccupancyFallback', rooms: fallback };
  }
}

export const exploreService = new ExploreService();
